import { Fragment, useEffect, useMemo, useState, useCallback } from 'react';

const BUCKET_COLORS = {
  VIP: 'bg-purple-100 text-purple-800 border-purple-300',
  HOT: 'bg-red-100 text-red-800 border-red-300',
  WARM: 'bg-orange-100 text-orange-800 border-orange-300',
  COLD: 'bg-blue-100 text-blue-800 border-blue-300',
  AT_RISK: 'bg-gray-100 text-gray-800 border-gray-300'
};

const FOLLOWUP_TYPES = ['CALL', 'EMAIL', 'WHATSAPP', 'SITE_VISIT', 'MEETING', 'PROPOSAL_FOLLOWUP', 'PAYMENT_REMINDER', 'GENERAL'];
const NOTE_TYPES = ['GENERAL', 'CALL_NOTE', 'MEETING_NOTE', 'NEGOTIATION', 'INTERNAL'];

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
const formatCurrency = (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '-';

function BanquetLeadDetail({ token, selectedProperty, selectedOutletId = '', leadId, onBack, onNavigateToConfig, onNavigateToQuotation }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const [lead, setLead] = useState(null);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Timeline pagination
  const [timeline, setTimeline] = useState([]);
  const [timelinePage, setTimelinePage] = useState(1);
  const [timelineTotal, setTimelineTotal] = useState(0);

  // Note form
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({ content: '', note_type: 'GENERAL', is_pinned: false });

  // Follow-up form
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpForm, setFollowUpForm] = useState({
    followup_type: 'CALL',
    scheduled_date: '',
    scheduled_time: '',
    priority: 'MEDIUM',
    description: ''
  });

  // Call log form
  const [showCallModal, setShowCallModal] = useState(false);
  const [callForm, setCallForm] = useState({ duration_minutes: '', outcome: '', notes: '' });

  // Lost reason modal
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostForm, setLostForm] = useState({ lost_reason: '', lost_reason_detail: '' });


  // Assign modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadLead = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}`, { headers });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || 'Failed to load lead');
      setLead(data);
    } catch (err) {
      setError(err?.message || 'Failed to load lead');
    } finally {
      setLoading(false);
    }
  }, [headers, leadId]);

  const loadTimeline = async (page = 1) => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/timeline?page=${page}&limit=20`, { headers });
      const { data } = await parseJson(res);
      if (res.ok) {
        setTimeline(data.timeline || []);
        setTimelineTotal(data.pagination?.total || 0);
        setTimelinePage(page);
      }
    } catch {}
  };

  const loadPipelineStages = async () => {
    try {
      const res = await fetch('/api/v1/banquet-crm/pipeline-stages', { headers });
      const { data } = await parseJson(res);
      if (res.ok) setPipelineStages(data.stages || []);
    } catch {}
  };

  const loadLostReasons = async () => {
    try {
      const res = await fetch('/api/v1/banquet-crm/lost-reasons', { headers });
      const { data } = await parseJson(res);
      if (res.ok) setLostReasons(data.reasons || []);
    } catch {}
  };

  const loadUsers = async () => {
    try {
      const res = await fetch('/api/v1/core/users', { headers });
      const { data } = await parseJson(res);
      if (res.ok) setUsers(Array.isArray(data) ? data : []);
    } catch {}
  };

  useEffect(() => {
    if (chainId && propertyId && leadId) {
      loadLead();
      loadTimeline(1);
      loadPipelineStages();
      loadLostReasons();
      loadUsers();
    }
  }, [chainId, propertyId, leadId]);

  const changeStage = async (newStage) => {
    if (newStage === 'LOST') {
      setShowLostModal(true);
      return;
    }
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/stage`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: newStage })
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to change stage');
      }
      setMessage('Stage updated');
      loadLead();
      loadTimeline(1);
    } catch (err) {
      setError(err?.message || 'Failed to change stage');
    }
  };

  const markLost = async () => {
    if (!lostForm.lost_reason) {
      setError('Please select a lost reason');
      return;
    }
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/mark-lost`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(lostForm)
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to mark as lost');
      }
      setMessage('Lead marked as lost');
      setShowLostModal(false);
      setLostForm({ lost_reason: '', lost_reason_detail: '' });
      loadLead();
      loadTimeline(1);
    } catch (err) {
      setError(err?.message || 'Failed to mark as lost');
    }
  };

  const assignLead = async () => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/assign`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: assignUserId || null })
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to assign lead');
      }
      setMessage('Lead assigned');
      setShowAssignModal(false);
      loadLead();
      loadTimeline(1);
    } catch (err) {
      setError(err?.message || 'Failed to assign lead');
    }
  };

  const recalculateScore = async () => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/score`, {
        method: 'POST',
        headers
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to recalculate score');
      }
      setMessage('Score recalculated');
      loadLead();
    } catch (err) {
      setError(err?.message || 'Failed to recalculate score');
    }
  };

  const addNote = async () => {
    if (!noteForm.content?.trim()) {
      setError('Note content is required');
      return;
    }
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/notes`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(noteForm)
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to add note');
      }
      setMessage('Note added');
      setShowNoteModal(false);
      setNoteForm({ content: '', note_type: 'GENERAL', is_pinned: false });
      loadLead();
      loadTimeline(1);
    } catch (err) {
      setError(err?.message || 'Failed to add note');
    }
  };

  const scheduleFollowUp = async () => {
    if (!followUpForm.scheduled_date) {
      setError('Scheduled date is required');
      return;
    }
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/followups`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(followUpForm)
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to schedule follow-up');
      }
      setMessage('Follow-up scheduled');
      setShowFollowUpModal(false);
      setFollowUpForm({ followup_type: 'CALL', scheduled_date: '', scheduled_time: '', priority: 'MEDIUM', description: '' });
      loadLead();
      loadTimeline(1);
    } catch (err) {
      setError(err?.message || 'Failed to schedule follow-up');
    }
  };

  const logCall = async () => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/log-call`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(callForm)
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to log call');
      }
      setMessage('Call logged');
      setShowCallModal(false);
      setCallForm({ duration_minutes: '', outcome: '', notes: '' });
      loadLead();
      loadTimeline(1);
    } catch (err) {
      setError(err?.message || 'Failed to log call');
    }
  };

  const completeFollowUp = async (followupId) => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/followups/${followupId}/complete`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to complete follow-up');
      }
      setMessage('Follow-up completed');
      loadLead();
      loadTimeline(1);
    } catch (err) {
      setError(err?.message || 'Failed to complete follow-up');
    }
  };

  if (loading && !lead) {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }

  if (!lead) {
    return (
      <div className="p-8 text-center">
        <div className="text-gray-500 mb-2">Lead not found</div>
        {error && <div className="text-red-600 text-sm">{error}</div>}
        <button onClick={onBack} className="mt-4 text-blue-600 hover:text-blue-800">← Back to Leads</button>
      </div>
    );
  }

  const currentStage = pipelineStages.find(s => s.stage_key === lead.pipeline_stage);

  // Days in current stage calculation
  const stageChangedDate = lead.stage_changed_at || lead.updated_at;
  const daysInStage = stageChangedDate
    ? Math.max(0, Math.floor((Date.now() - new Date(stageChangedDate).getTime()) / (1000 * 60 * 60 * 24)))
    : 0;
  const stageSlaHours = currentStage?.sla_hours || 0;
  const slaBreach = stageSlaHours > 0 && (daysInStage * 24 > stageSlaHours);

  // Check if lead stage is BOOKED or later (sort_order >= 9)
  const currentSortOrder = currentStage?.sort_order || 0;
  const showEventTasksTab = currentSortOrder >= 9;

  // Build tabs list dynamically
  const tabs = ['overview', 'timeline', 'notes', 'followups', 'communication'];
  if (showEventTasksTab) tabs.push('event_tasks');

  return (
    <div className="p-4 max-w-full">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <button onClick={onBack} className="text-blue-600 hover:text-blue-800">← Back</button>
        <h2 className="text-xl font-bold text-gray-800">{lead.lead_no}</h2>
        <select
          value={lead.lead_bucket || 'COLD'}
          onChange={async (e) => {
            try {
              const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/bucket`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ bucket: e.target.value })
              });
              if (res.ok) { loadLead(); setMessage('Bucket updated'); }
              else { const d = await res.json(); setError(d.error || 'Failed to update bucket'); }
            } catch { setError('Failed to update bucket'); }
          }}
          className={`px-3 py-1 text-sm rounded-full border cursor-pointer ${BUCKET_COLORS[lead.lead_bucket] || BUCKET_COLORS.COLD}`}
        >
          {['VIP', 'HOT', 'WARM', 'COLD', 'AT_RISK'].map(b => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <span className="px-3 py-1 text-sm rounded" style={{ backgroundColor: currentStage?.stage_color ? `${currentStage.stage_color}30` : '#f3f4f6' }}>
          {currentStage?.stage_name || lead.pipeline_stage}
        </span>
        <div className="ml-auto">
          <button
            onClick={() => onNavigateToConfig && onNavigateToConfig()}
            title="CRM Configuration"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      {message && <div className="mb-3 p-3 bg-green-100 text-green-700 rounded">{message}</div>}

      <LeadArbitrationBanner leadId={leadId} headers={headers} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Tabs */}
          <div className="bg-white rounded-lg shadow">
            <div className="border-b flex flex-wrap">
              {tabs.map(tab => {
                const labels = {
                  overview: 'Overview',
                  timeline: 'Timeline',
                  notes: 'Notes',
                  followups: 'Follow-ups',
                  communication: 'Communication',
                  event_tasks: 'Event Tasks'
                };
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-3 text-sm font-medium ${activeTab === tab ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {labels[tab] || tab}
                  </button>
                );
              })}
            </div>

            <div className="p-4">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-500 text-sm">Client</h4>
                      <p className="font-semibold">{lead.client_name}</p>
                      <p className="text-sm text-gray-600">{lead.client_mobile || '-'}</p>
                      <p className="text-sm text-gray-600">{lead.client_email || '-'}</p>
                      {lead.client_company && <p className="text-sm text-gray-600">{lead.client_company}</p>}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-500 text-sm">Event</h4>
                      <p className="font-semibold">{lead.event_type || '-'}</p>
                      <p className="text-sm text-gray-600">{lead.event_name || '-'}</p>
                      <p className="text-sm text-gray-600">
                        {formatDate(lead.event_from_date)}{lead.event_to_date && lead.event_to_date !== lead.event_from_date ? ` - ${formatDate(lead.event_to_date)}` : ''}
                      </p>
                      {lead.expected_pax && <p className="text-sm text-gray-600">{lead.expected_pax} pax</p>}
                    </div>
                  </div>

                  <div className="border-t pt-4 grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-500 text-sm">Budget</h4>
                      <p>{lead.budget_min ? `${formatCurrency(lead.budget_min)} - ` : ''}{formatCurrency(lead.budget_max)}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-500 text-sm">Lead Source</h4>
                      <p>{lead.lead_source}{lead.lead_source_detail ? ` (${lead.lead_source_detail})` : ''}</p>
                    </div>
                  </div>

                  {(lead.food_requirement || lead.decor_requirement || lead.special_requests) && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-gray-500 text-sm mb-2">Requirements</h4>
                      {lead.food_requirement && <p className="text-sm"><span className="text-gray-500">Food:</span> {lead.food_requirement}</p>}
                      {lead.decor_requirement && <p className="text-sm"><span className="text-gray-500">Decor:</span> {lead.decor_requirement}</p>}
                      {lead.special_requests && <p className="text-sm"><span className="text-gray-500">Special:</span> {lead.special_requests}</p>}
                    </div>
                  )}

                  {lead.is_room_required && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-gray-500 text-sm">Room Block</h4>
                      <p>{lead.num_rooms || 0} rooms required</p>
                    </div>
                  )}

                  {lead.notes && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-gray-500 text-sm">Internal Notes</h4>
                      <p className="text-sm">{lead.notes}</p>
                    </div>
                  )}

                  {/* Linked Records */}
                  {(lead.quotation_id || lead.booking_id) && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-gray-500 text-sm mb-2">Linked Records</h4>
                      {lead.quotation_no && <p className="text-sm">Quotation: <span className="text-blue-600">{lead.quotation_no}</span></p>}
                      {lead.booking_no && <p className="text-sm">Booking: <span className="text-blue-600">{lead.booking_no}</span></p>}
                    </div>
                  )}
                </div>
              )}

              {/* Timeline Tab */}
              {activeTab === 'timeline' && (
                <div className="space-y-3">
                  {timeline.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No activity yet</p>
                  ) : timeline.map(item => (
                    <div key={item.timeline_id} className="flex gap-3 pb-3 border-b last:border-0">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mt-2"></div>
                      <div className="flex-1">
                        <p className="text-sm">{item.event_detail}</p>
                        <p className="text-xs text-gray-500">{formatDateTime(item.created_at)} {item.created_by_name ? `by ${item.created_by_name}` : ''}</p>
                      </div>
                    </div>
                  ))}
                  {timelineTotal > timeline.length && (
                    <button
                      onClick={() => loadTimeline(timelinePage + 1)}
                      className="w-full text-center text-blue-600 text-sm py-2"
                    >
                      Load more
                    </button>
                  )}
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="space-y-3">
                  <button
                    onClick={() => setShowNoteModal(true)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    + Add Note
                  </button>
                  {(lead.recent_notes || []).length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No notes yet</p>
                  ) : (lead.recent_notes || []).map(note => (
                    <div key={note.note_id} className={`p-3 rounded ${note.is_pinned ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-gray-500">{note.note_type}</span>
                        {note.is_pinned && <span className="text-xs text-yellow-600">📌 Pinned</span>}
                      </div>
                      <p className="text-sm">{note.content}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatDateTime(note.created_at)} {note.created_by_name ? `by ${note.created_by_name}` : ''}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Follow-ups Tab */}
              {activeTab === 'followups' && (
                <div className="space-y-3">
                  <button
                    onClick={() => setShowFollowUpModal(true)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    + Schedule Follow-up
                  </button>
                  {(lead.upcoming_followups || []).length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No upcoming follow-ups</p>
                  ) : (lead.upcoming_followups || []).map(fu => (
                    <div key={fu.followup_id} className={`p-3 rounded flex items-center justify-between ${fu.status === 'OVERDUE' ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{fu.followup_type}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${fu.priority === 'HIGH' ? 'bg-red-100 text-red-700' : fu.priority === 'LOW' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>
                            {fu.priority}
                          </span>
                          {fu.status === 'OVERDUE' && <span className="text-xs text-red-600">OVERDUE</span>}
                        </div>
                        <p className="text-sm text-gray-600">{formatDate(fu.scheduled_date)} {fu.scheduled_time || ''}</p>
                        {fu.description && <p className="text-xs text-gray-500">{fu.description}</p>}
                      </div>
                      <button
                        onClick={() => completeFollowUp(fu.followup_id)}
                        className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                      >
                        Complete
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Communication Tab */}
              {activeTab === 'communication' && (
                <div className="text-center py-12">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500">
                    Communication features (Email/WhatsApp templates, message history) will be available in Phase 2.
                  </p>
                </div>
              )}

              {/* Event Tasks Tab */}
              {activeTab === 'event_tasks' && (
                <div className="text-center py-12">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  <p className="text-gray-500">
                    Event task management (pre-event checklists) will be available after the lead reaches BOOKED stage.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-medium mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <button
                onClick={() => setShowCallModal(true)}
                className="w-full px-3 py-2 text-left text-sm border rounded hover:bg-gray-50"
              >
                📞 Log Call
              </button>
              <button
                onClick={() => setShowFollowUpModal(true)}
                className="w-full px-3 py-2 text-left text-sm border rounded hover:bg-gray-50"
              >
                ⏰ Schedule Follow-up
              </button>
              <button
                onClick={() => setShowNoteModal(true)}
                className="w-full px-3 py-2 text-left text-sm border rounded hover:bg-gray-50"
              >
                📝 Add Note
              </button>
              {!lead.quotation_id && onNavigateToQuotation && (
                <button
                  onClick={() => onNavigateToQuotation({
                    lead_id: lead.lead_id,
                    lead_no: lead.lead_no,
                    customer_name: lead.client_name || '',
                    customer_phone: lead.client_mobile || '',
                    customer_email: lead.client_email || '',
                    expected_pax: lead.expected_pax || '',
                    event_name: lead.event_name || '',
                    event_start_date: lead.event_from_date ? new Date(lead.event_from_date).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) : '',
                    event_end_date: lead.event_to_date ? new Date(lead.event_to_date).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) : '',
                    event_from_time: lead.event_start_time ? String(lead.event_start_time).slice(0, 5) : '',
                    event_to_time: lead.event_end_time ? String(lead.event_end_time).slice(0, 5) : '',
                    notes: lead.notes || '',
                    venue: lead.preferred_hall_name || ''
                  })}
                  className="w-full px-3 py-2 text-left text-sm border rounded hover:bg-blue-50 text-blue-700 font-medium"
                >
                  📄 Create Quotation
                </button>
              )}
              {lead.quotation_id && onNavigateToQuotation && (
                <button
                  onClick={() => onNavigateToQuotation({ view_quotation_id: lead.quotation_id })}
                  className="w-full px-3 py-2 text-left text-sm border rounded hover:bg-green-50 text-green-700 font-medium"
                >
                  📄 View Quotation
                </button>
              )}
            </div>
          </div>

          {/* Stage Change */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-medium mb-3">Pipeline Stage</h3>
            <select
              value={lead.pipeline_stage}
              onChange={(e) => changeStage(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            >
              {pipelineStages.filter(s => s.is_active).map(s => (
                <option key={s.stage_key} value={s.stage_key}>{s.stage_name}</option>
              ))}
            </select>
            <div className="mt-3 flex items-center justify-between">
              <span className={`text-sm ${slaBreach ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                {slaBreach && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1 -mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                Days in stage: {daysInStage}
              </span>
              {slaBreach && (
                <span className="px-2 py-0.5 text-xs font-bold bg-red-100 text-red-700 border border-red-300 rounded">
                  SLA BREACH
                </span>
              )}
            </div>
          </div>

          {/* Assignment */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-medium mb-3">Assigned To</h3>
            <div className="flex items-center justify-between">
              <span>{lead.assigned_to_name || 'Unassigned'}</span>
              <button
                onClick={() => {
                  setAssignUserId(lead.assigned_to || '');
                  setShowAssignModal(true);
                }}
                className="text-blue-600 text-sm hover:text-blue-800"
              >
                Change
              </button>
            </div>
          </div>

          {/* Score */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium">Lead Score</h3>
              <button
                onClick={recalculateScore}
                className="text-blue-600 text-xs hover:text-blue-800"
              >
                Recalculate
              </button>
            </div>
            <div className="text-3xl font-bold text-center mb-2">{lead.score_overall || 0}</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Conversion</span>
                <span>{lead.score_conversion || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Revenue</span>
                <span>{lead.score_revenue || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Urgency</span>
                <span>{lead.score_urgency || 0}</span>
              </div>
            </div>
          </div>

          {/* Key Dates */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-medium mb-3">Key Dates</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Lead Date</span>
                <span>{formatDate(lead.lead_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Contact</span>
                <span>{formatDateTime(lead.last_contacted_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Next Follow-up</span>
                <span>{formatDate(lead.next_followup_date)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Add Note</h3>
              <button onClick={() => setShowNoteModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Note Type</label>
                <select
                  value={noteForm.note_type}
                  onChange={(e) => setNoteForm({ ...noteForm, note_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  {NOTE_TYPES.map(t => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Content</label>
                <textarea
                  value={noteForm.content}
                  onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                  rows="4"
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="pin_note"
                  checked={noteForm.is_pinned}
                  onChange={(e) => setNoteForm({ ...noteForm, is_pinned: e.target.checked })}
                />
                <label htmlFor="pin_note" className="text-sm">Pin this note</label>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowNoteModal(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={addNote} className="px-4 py-2 bg-blue-600 text-white rounded">Add Note</button>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Modal */}
      {showFollowUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Schedule Follow-up</h3>
              <button onClick={() => setShowFollowUpModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select
                    value={followUpForm.followup_type}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, followup_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    {FOLLOWUP_TYPES.map(t => (
                      <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Priority</label>
                  <select
                    value={followUpForm.priority}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, priority: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input
                    type="date"
                    value={followUpForm.scheduled_date}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, scheduled_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Time</label>
                  <input
                    type="time"
                    value={followUpForm.scheduled_time}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, scheduled_time: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={followUpForm.description}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, description: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowFollowUpModal(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={scheduleFollowUp} className="px-4 py-2 bg-blue-600 text-white rounded">Schedule</button>
            </div>
          </div>
        </div>
      )}

      {/* Call Log Modal */}
      {showCallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Log Call</h3>
              <button onClick={() => setShowCallModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Duration (min)</label>
                  <input
                    type="number"
                    value={callForm.duration_minutes}
                    onChange={(e) => setCallForm({ ...callForm, duration_minutes: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Outcome</label>
                  <select
                    value={callForm.outcome}
                    onChange={(e) => setCallForm({ ...callForm, outcome: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="">Select...</option>
                    <option value="CONNECTED">Connected</option>
                    <option value="NO_ANSWER">No Answer</option>
                    <option value="BUSY">Busy</option>
                    <option value="LEFT_VOICEMAIL">Left Voicemail</option>
                    <option value="WRONG_NUMBER">Wrong Number</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={callForm.notes}
                  onChange={(e) => setCallForm({ ...callForm, notes: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowCallModal(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={logCall} className="px-4 py-2 bg-blue-600 text-white rounded">Log Call</button>
            </div>
          </div>
        </div>
      )}

      {/* Lost Reason Modal */}
      {showLostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Mark as Lost</h3>
              <button onClick={() => setShowLostModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Lost Reason *</label>
                <select
                  value={lostForm.lost_reason}
                  onChange={(e) => setLostForm({ ...lostForm, lost_reason: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">Select reason...</option>
                  {lostReasons.filter(r => r.is_active).map(r => (
                    <option key={r.reason_key} value={r.reason_key}>{r.reason_label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Details</label>
                <textarea
                  value={lostForm.lost_reason_detail}
                  onChange={(e) => setLostForm({ ...lostForm, lost_reason_detail: e.target.value })}
                  rows="3"
                  placeholder="Additional details..."
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowLostModal(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={markLost} className="px-4 py-2 bg-red-600 text-white rounded">Mark as Lost</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Assign Lead</h3>
              <button onClick={() => setShowAssignModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4">
              <label className="block text-sm font-medium mb-1">Assign To</label>
              <select
                value={assignUserId}
                onChange={(e) => setAssignUserId(e.target.value)}
                className="w-full px-3 py-2 border rounded"
              >
                <option value="">Unassigned</option>
                {users.map(u => (
                  <option key={u.user_id} value={u.user_id}>{u.name_of_user || u.username}</option>
                ))}
              </select>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowAssignModal(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={assignLead} className="px-4 py-2 bg-blue-600 text-white rounded">Assign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Lead Arbitration banner (SRS_AI_BANQUET_LEAD_ARBITRATION §7.2) ────────
// Shows on the lead-detail when this lead is a member of one or more
// open clusters. Lists the competing leads with their ranks + revenue so
// the manager understands the context before acting.
function LeadArbitrationBanner({ leadId, headers }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let abort = false;
    if (!leadId) return;
    (async () => {
      try {
        const r = await fetch(`/api/v1/banquet-crm/lead-arbitration/leads/${leadId}/clusters`, { headers });
        if (!r.ok) return;
        const body = await r.json();
        if (!abort) setData(body.clusters || []);
      } catch { /* silent — banner is optional */ }
    })();
    return () => { abort = true; };
  }, [leadId, headers]);

  if (!data || data.length === 0) return null;
  // Show the most competitive cluster (lowest rank wins prominence).
  const primary = [...data].sort((a, b) => (a.rank_in_cluster || 99) - (b.rank_in_cluster || 99))[0];
  const isWinner = primary.is_recommended;
  const bg = isWinner ? '#f0fdf4' : '#fef3c7';
  const fg = isWinner ? '#166534' : '#92400e';
  const border = isWinner ? '#bbf7d0' : '#fcd34d';

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, color: fg,
      borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>
          {isWinner ? '⭐ Recommended Lead' : `🥈 Ranked #${primary.rank_in_cluster}`}
        </span>
        <span>
          competing with {primary.member_count - 1} other lead{primary.member_count > 2 ? 's' : ''}
          {' '}for <b>{primary.hall_name || `Hall #${primary.hall_id}`}</b>
          {' '}on <b>{primary.event_from_date}{primary.event_from_date !== primary.event_to_date ? `–${primary.event_to_date}` : ''}</b>
        </span>
      </div>
      {data.length > 1 && (
        <div style={{ marginTop: 4, fontSize: 12 }}>
          Also competing in {data.length - 1} other hall cluster{data.length > 2 ? 's' : ''} (hall-less lead → feasibility-matched).
        </div>
      )}
    </div>
  );
}

export default BanquetLeadDetail;
